import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Crown, Check, MessageCircle, LogIn, AlertTriangle } from "lucide-react";

const PRO_FEATURES = [
  "Unlimited AI predictions & analysis",
  "All high-impact data: NFP, CPI, Fed, PCE, GDP, wages",
  "Pre-release target mode for every event",
  "Push alerts ~30 min before releases",
  "Full verified track record",
];

export function UpgradeDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const { user, isPro, login, refresh } = useAuth();

  useEffect(() => {
    if (open) api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="upgrade-dialog" className="bg-[#0A0D14] border-white/10 text-[#F9FAFB] max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Crown size={22} className="text-[#F59E0B]" /> Go Pro
          </DialogTitle>
          <DialogDescription className="text-sm text-[#9CA3AF] leading-relaxed">
            Full access for 3 days — just {cfg?.pricing?.intro?.replace("$1.99 for 3 days", "$1.99") || "$1.99"}. Then {cfg?.pricing?.monthly || "$29.85/mo"} ({cfg?.pricing?.discount || "50% off"} {cfg?.pricing?.list || "$59.70"}). Cancel anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="glass rounded-2xl p-4 mb-2">
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-mono-num text-4xl font-bold text-[#F59E0B]">$1.99</span>
            <span className="text-sm text-[#9CA3AF]">for 3 days, then {cfg?.pricing?.monthly || "$29.85/mo"}</span>
          </div>
          <ul className="space-y-2.5">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-base text-[#D1D5DB]">
                <Check size={17} className="text-[#10B981] mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>

        {isPro ? (
          <div className="rounded-xl bg-[#10B981]/10 border border-[#10B981]/30 p-4 text-center text-sm text-[#10B981] font-semibold">
            ✓ You're on Pro — full access unlocked.
          </div>
        ) : !user ? (
          <button data-testid="upgrade-signin" onClick={login}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-white text-black font-semibold text-sm hover:bg-white/90 transition-colors">
            <LogIn size={16} /> Sign in with Google to subscribe
          </button>
        ) : cfg?.paypal_enabled ? (
          <div data-testid="paypal-button-container">
            <PayPalScriptProvider options={{ clientId: cfg.paypal_client_id, vault: true, intent: "subscription" }}>
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "pill", label: "subscribe" }}
                createSubscription={(data, actions) => actions.subscription.create({ plan_id: cfg.paypal_plan_id })}
                onApprove={async (data) => {
                  try {
                    let res = await api.post("/subscription/activate", { subscription_id: data.subscriptionID });
                    for (let i = 0; i < 6 && !res.data?.ok && res.data?.pending; i++) {
                      toast.message("Payment approved — waiting for PayPal to confirm the first charge…", { id: "pp-pending" });
                      await new Promise((r) => setTimeout(r, 5000));
                      res = await api.post("/subscription/activate", { subscription_id: data.subscriptionID });
                    }
                    if (res.data?.ok) {
                      toast.success("Welcome to Pro! Subscription active.", { id: "pp-pending" });
                      await refresh();
                      setOpen(false);
                    } else if (res.data?.pending) {
                      toast.message("PayPal is still confirming your payment. Pro unlocks automatically once it's confirmed — reopen the app in a minute.", { id: "pp-pending", duration: 10000 });
                    } else {
                      toast.error("Could not verify the subscription. Please contact support via WhatsApp.", { id: "pp-pending" });
                    }
                  } catch (e) {
                    toast.error("Activation failed. Please try again or contact support.");
                  }
                }}
                onError={() => toast.error("PayPal checkout failed. Please try again.")}
              />
            </PayPalScriptProvider>
            <p className="text-sm text-center text-[#9CA3AF] mt-2">Secure recurring billing by PayPal · cancel anytime</p>
          </div>
        ) : (
          <div data-testid="paypal-pending" className="rounded-xl border border-dashed border-white/15 p-3 text-center text-sm text-[#9CA3AF]">
            PayPal checkout will appear here once billing is configured.
          </div>
        )}

        {cfg?.whatsapp_link && (
          <a data-testid="whatsapp-subscribe" href={cfg.whatsapp_link} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full mt-2 py-3 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] font-semibold text-base hover:bg-[#25D366]/25 transition-colors">
            <MessageCircle size={18} /> Subscribe / ask via WhatsApp
          </a>
        )}

        <div data-testid="subscription-disclaimer" className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3.5 text-sm text-[#9CA3AF] leading-relaxed space-y-2">
          <p className="font-semibold text-[#D1D5DB] flex items-center gap-1.5"><AlertTriangle size={15} className="text-[#F59E0B]" /> Disclaimer</p>
          <p>
            <span className="text-[#D1D5DB] font-medium">Not financial advice.</span> XAUUSD AI Terminal provides AI-generated market analysis and
            signals for educational and informational purposes only. Trading gold and other financial instruments involves
            substantial risk of loss and is not suitable for every investor. Past performance does not guarantee future results.
            You are solely responsible for your trading decisions.
          </p>
          <p>
            <span className="text-[#D1D5DB] font-medium">Billing terms.</span> Your subscription starts with a 3-day introductory
            period, then renews automatically each month at {cfg?.pricing?.monthly || "$29.85/mo"} until cancelled. You can cancel
            anytime from your PayPal account; access continues until the end of the paid period. Payments are processed securely
            by PayPal — we never store your card details.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
